import {
  Box,
  Card,
  Heading,
  Icon,
  LoadingMask,
  Paragraph,
  Table,
  Text,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { useQuery } from '@zg-rentals/react-query5';
import {
  MoodysAnalyticsWebApiHandler_getMoodysAnalyticsForRegion,
  MoodysAnalyticsWebApiHandler_getMoodysAnalyticsForRegion_Path,
} from '@zg-rentals/ts-customer-analytics-client';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import { useEffect } from 'react';
import { roundUp } from '../../../../utils/roundUp';
import { localDateToDate } from '../../../../utils/localDateToDate';
import { format, subQuarters } from 'date-fns';
import { IconArrowDownFilled, IconArrowUpFilled } from '@zillow/constellation-icons';
import { DisplayingRegionLevel } from '../../../../types/DisplayingRegionLevel';
import { CompetitorsSidebarTable } from './CompetitorsSidebarTable';
import { formatNumber } from '../../../../utils';
import { getRegionIdKeyFromRegionLevel } from '../../../../utils/getRegionIdKeyFromRegionLevel';
import { MarketDataMapSidebarEmptyStateCard } from './MarketDataMapSidebarEmptyState';
import { US_REGION_ID } from '../../../../constants';

const SidebarContent = ({ children, loading = false }: { children: React.ReactNode; loading?: boolean }) => {
  return (
    <LoadingMask loading={loading} css={{ width: '100%', '& > div': { width: '100%' } }}>
      <Box css={{ padding: '400', display: 'flex', flexDirection: 'column', gap: '400' }}>{children}</Box>
    </LoadingMask>
  );
};

const quarterDateFormat = 'QQQ yyyy';
const previousQuarter = format(subQuarters(new Date(), 1), quarterDateFormat);

export const MarketDataMapSidebar = () => {
  const {
    filteredProperties,
    regionsMapping,
    selectedRegionId,
    selectedDisplayingRegionLevel,
    visibleRegionIds,
    displayMode,
  } = useMainLayout();
  const { enqueueToast } = useToast();
  const { data, isLoading, error } = useQuery({
    queryKey: [MoodysAnalyticsWebApiHandler_getMoodysAnalyticsForRegion_Path, US_REGION_ID],
    queryFn: () =>
      MoodysAnalyticsWebApiHandler_getMoodysAnalyticsForRegion({
        lookbackQuarters: 2,
        zillowRegionIds: [US_REGION_ID],
      }),
  });

  useEffect(() => {
    if (error) {
      const errorMessage = DatarouterError.getDisplayMessage(error, 'Error fetching US vacancy rate');
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  }, [error, enqueueToast]);

  const finalDisplayMode = selectedRegionId ? selectedDisplayingRegionLevel : displayMode;
  const statsKey = getRegionIdKeyFromRegionLevel(finalDisplayMode);
  const moodysAnalytics = data && data.length > 0 ? (data.find((data) => data.hasVacancyRate) ?? null) : null;
  const allUSVacancyRate = moodysAnalytics?.vacancyRate ? moodysAnalytics?.vacancyRate : 0;
  const previousQuarterStats = new Map<number, Array<number>>();
  const visibleProperties = filteredProperties.filter((property) =>
    visibleRegionIds.some(
      (region) =>
        region.regionId === property.propertyDetails.stateRegionId ||
        region.regionId === property.propertyDetails.metroStatRegionId ||
        region.regionId === property.propertyDetails.cityRegionId ||
        region.regionId === property.propertyDetails.zipRegionId,
    ),
  );

  const hasNoProperties = visibleProperties.length === 0;

  visibleProperties.forEach((property) => {
    property.breakdownStats.forEach((stat) => {
      const statDate = localDateToDate(stat.statDate);
      const formattedQuarter = format(statDate, quarterDateFormat);
      if (formattedQuarter === previousQuarter) {
        const vacancyRate = (stat.advertisedUnitsAverage || 0) / (property.propertyDetails.numUnits || 1);
        const regionId = Number(property.propertyDetails[statsKey]);
        const previousQuarterStatsForRegion = previousQuarterStats.get(regionId);
        if (previousQuarterStatsForRegion) {
          previousQuarterStatsForRegion.push(vacancyRate);
        } else {
          previousQuarterStats.set(regionId, [vacancyRate]);
        }
      }
    });
  });

  const allRegionIdsForLevel = [
    ...new Set(visibleProperties.map((property) => Number(property.propertyDetails[statsKey]))),
  ].filter((regionId) => !!regionId);

  const currentVacancyRate = new Map<number, number>();

  allRegionIdsForLevel.forEach((regionId) => {
    const propertiesInRegion = visibleProperties.filter((property) => property.propertyDetails[statsKey] === regionId);

    const averageVacancyRate =
      propertiesInRegion.reduce(
        (acc, property) =>
          acc + (property.aggregateStats.advertisedUnitsAverage ?? 0) / (property.propertyDetails.numUnits || 1),
        0,
      ) / propertiesInRegion.length;

    currentVacancyRate.set(regionId, averageVacancyRate);
  });

  const previousQuarterStatsVacancyRateMapping = new Map<number, number>();
  previousQuarterStats.forEach((stats, regionId) => {
    const vacancyRate = stats.reduce((acc, stat) => acc + stat, 0) / (stats?.length || 1);
    previousQuarterStatsVacancyRateMapping.set(regionId, vacancyRate);
  });

  const activeUnitsInPortfolio = visibleProperties.reduce(
    (acc, property) => acc + (property.propertyDetails.numUnits ?? 0),
    0,
  );

  const zillowRegionIds = [
    ...new Set(visibleProperties.map((property) => Number(property.propertyDetails[statsKey]))),
  ].filter((regionId) => !!regionId);

  const { data: marketData, isLoading: isLoadingMarketData } = useQuery({
    queryKey: [MoodysAnalyticsWebApiHandler_getMoodysAnalyticsForRegion_Path, { zillowRegionIds }],
    queryFn: () =>
      MoodysAnalyticsWebApiHandler_getMoodysAnalyticsForRegion({
        lookbackQuarters: 4,
        zillowRegionIds,
      }),
    enabled:
      [DisplayingRegionLevel.CITY, DisplayingRegionLevel.ZIP].includes(finalDisplayMode) && zillowRegionIds.length > 0,
  });
  const marketVacantUnits = marketData?.find((data) => data.hasVacantUnits)?.vacantUnits ?? 0;

  return (
    <SidebarContent loading={isLoading}>
      {[DisplayingRegionLevel.CITY, DisplayingRegionLevel.ZIP].includes(finalDisplayMode) && !hasNoProperties && (
        <LoadingMask loading={isLoadingMarketData} css={{ width: '100%', '& > div': { width: '100%' } }}>
          <Card elevated={false}>
            <Paragraph textStyle="body-sm-bold">Active units in portfolio</Paragraph>
            <Heading level={5}>{formatNumber(Number(activeUnitsInPortfolio.toFixed(0)))}</Heading>
          </Card>
          <Card elevated={false} css={{ marginTop: '400' }}>
            <Paragraph textStyle="body-sm-bold">Active units in market</Paragraph>
            <Heading level={5}>
              {!isLoadingMarketData && marketVacantUnits === 0
                ? 'No data'
                : formatNumber(Number(marketVacantUnits.toFixed(0)))}
            </Heading>
          </Card>
        </LoadingMask>
      )}
      {[DisplayingRegionLevel.STATE, DisplayingRegionLevel.METRO_AREA, DisplayingRegionLevel.COUNTRY].includes(
        finalDisplayMode,
      ) && (
        <Card elevated={false}>
          <Paragraph textStyle="body-sm-bold">US vacancy rate</Paragraph>
          <Heading level={5}>{roundUp(allUSVacancyRate * 100, 1)}%</Heading>
        </Card>
      )}
      {hasNoProperties ? (
        <MarketDataMapSidebarEmptyStateCard />
      ) : (
        <>
          {finalDisplayMode !== DisplayingRegionLevel.ZIP && (
            <Table.Root aria-label="Market data table">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Advertising markets</Table.HeaderCell>
                  <Table.HeaderCell css={{ textAlign: 'right' }}>Vacancy rate & QoQ</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {allRegionIdsForLevel.map((regionId) => {
                  const vacancyRate = currentVacancyRate.get(regionId) ?? 0;
                  const previousQuarterVacancyRate = previousQuarterStatsVacancyRateMapping.get(regionId) ?? 0;
                  const qoqVacancyRate = vacancyRate - previousQuarterVacancyRate;
                  const isIncreasing = qoqVacancyRate >= 0;
                  const regionName = regionsMapping[regionId];
                  let qoqDifferenceLabel = (
                    <Text textStyle="body-sm-bold" css={{ color: 'teal.600' }}>
                      <Icon size="sm" css={{ position: 'relative', top: '1px' }}>
                        <IconArrowUpFilled />
                      </Icon>
                      {roundUp(Math.abs(qoqVacancyRate) * 100, 1).toFixed(1)}%
                    </Text>
                  );

                  if (qoqVacancyRate === 0) {
                    qoqDifferenceLabel = (
                      <Text textStyle="body-sm-bold" css={{ color: 'gray.600' }}>
                        {roundUp(Math.abs(qoqVacancyRate) * 100, 1).toFixed(1)}%
                      </Text>
                    );
                  } else if (!isIncreasing) {
                    qoqDifferenceLabel = (
                      <Text textStyle="body-sm-bold" css={{ color: 'red.600' }}>
                        <Icon size="sm" css={{ position: 'relative', top: '1px' }}>
                          <IconArrowDownFilled />
                        </Icon>
                        {roundUp(Math.abs(qoqVacancyRate) * 100, 1).toFixed(1)}%
                      </Text>
                    );
                  }

                  return (
                    <Table.Row key={regionId}>
                      <Table.Cell>{regionName}</Table.Cell>
                      <Table.Cell>
                        <Paragraph css={{ textAlign: 'right' }}>
                          {vacancyRate > 1 ? (
                            <Text textStyle="body-sm-bold" css={{ display: 'inline-block', color: 'gray.600' }}>
                              No data
                            </Text>
                          ) : (
                            <>
                              <Text textStyle="body-sm" css={{ display: 'inline-block' }}>
                                {roundUp(vacancyRate * 100, 1)}%
                              </Text>
                              {qoqDifferenceLabel}
                            </>
                          )}
                        </Paragraph>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>
          )}
          {finalDisplayMode === DisplayingRegionLevel.ZIP && (
            <CompetitorsSidebarTable finalDisplayMode={finalDisplayMode} />
          )}
        </>
      )}
    </SidebarContent>
  );
};
